/**
 * Low-level reactivity primitives — additive escape hatch alongside `use()`.
 *
 * These bypass the hook system and the component-mount overhead (~14 μs per
 * `use(0)` call in Phase 0 measurements). Use them for framework-level state,
 * detached effects, or hot loops that mount/teardown signals at high frequency.
 *
 * Component-scoped state should still use `use()` — it integrates with hooks,
 * the global registry, devtools, and component-lifecycle cleanup.
 *
 * @example
 * import { createSignal, createEffect } from 'flexium/core'
 *
 * const [count, setCount] = createSignal(0)
 * createEffect(() => console.log('count is', count()))
 * setCount(c => c + 1)   // → logs "count is 1" on next microtask
 */

import {
    createSignalV2,
    createEffectV2,
} from './reactive-v2'
import { createComputedV2 } from './computed-v2'
import { scheduleEffect } from './scheduler'
import type { Setter } from './use'

export type Signal<T> = readonly [() => T, Setter<T>]

/**
 * Create a reactive signal with a stable [getter, setter] tuple.
 *
 * Unlike `use(value)`, this is NOT tied to a component — it can be created at
 * module scope or inside arbitrary functions. The signal lives until all of
 * its subscribers are stopped and the tuple is garbage-collected.
 */
export function createSignal<T>(initialValue: T): Signal<T> {
    const sc = createSignalV2(initialValue, scheduleEffect)
    return [sc._getter, sc._setter as Setter<T>] as const
}

/**
 * Create an effect that runs immediately and re-runs when any signal it reads
 * changes. Unlike `unsafeEffect()`, this is not auto-registered with a
 * component instance — it lives until `.stop()` is called on the returned
 * handle.
 *
 * @returns a handle with `.stop()` to permanently dispose the effect
 */
export function createEffect(fn: () => void): { stop: () => void } {
    const runner = createEffectV2(fn, { attachToComponent: false })
    return { stop: () => runner.effect.stop() }
}

/**
 * Create a lazy computed signal — a derived getter that re-evaluates only
 * when one of its deps changes AND the value is actually read.
 *
 * @returns a `() => T` getter; call `.stop` on the returned handle to dispose
 */
export function createComputed<T>(fn: () => T): { get: () => T; stop: () => void } {
    const c = createComputedV2(fn, scheduleEffect)
    return { get: c.get, stop: c.stop }
}
